import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';

import Select from '../../../../components/forms/Select';
import {
  brokerRateEditRoles,
  BrokerRateFilters,
  BrokerRateModel,
  BrokerRatesStatisticsModel,
  ItemModel,
  paymentTypeOptions,
  ProductModel,
  rateTypeOptions
} from '../../../../api/core/core.types';
import StatusLabel from '../../../../components/forms/StatusLabel';
import { BrokerRateActionTypes } from '../../types/brokerrate.types';
import { getBrokerRateList, updateBrokerRateFilters } from '../../actions/brokerrate.actions';
import { getBrokerList } from '../../../admin/actions/broker.actions';
import { getProductList } from '../../actions/product.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';
import { WithTranslation, withTranslation } from 'react-i18next';
import { LoaderBar } from '../../../../components/forms/LoaderBar';
import { CoreApi } from '../../../../api/api.apiclient';
import dayjs from 'dayjs';
import infoIcon from '../../../../assets/img/info-circle.svg';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import { ItemPriceModel } from '../../../../api/pricing/pricing.types';
import { getItemPriceList } from '../../../rates/actions/itemprice.actions';
import { getItemList } from '../../actions/item.actions';
import ListViewTable from '../../../../components/tables/ListViewTable';
import { SupplierBrokerModel } from '../../../../api/admin/admin.types';
import { getSupplierBrokerList } from '../../../admin/actions/supplierbroker.actions';

interface BrokerRateProps extends RouteComponentProps, WithTranslation {
  getBrokerRateList: (filters?: BrokerRateFilters, page?: PageRequest) => Promise<any>;
  getItemPriceList: () => void;
  getItemList: () => void;
  getBrokerList: () => void;
  getProductList: (isDeletedIncluded: boolean) => Promise<void>;
  updateBrokerRateFilters: (filters: BrokerRateFilters) => void;
  brokerRates: BrokerRateModel[] | null;
  brokers: SupplierBrokerModel[];
  products: ProductModel[];
  items: ItemModel[];
  itemPrices: ItemPriceModel[];
  user: UserModel | null;
  totalElements: number;
  totalPages: number;
  filters: BrokerRateFilters | null;
}

interface BrokerRateState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string;
  filtersUpdated: boolean;
  loading: boolean;
  statistics: BrokerRatesStatisticsModel[];
  filter: {
    combinedFilter: {
      value: string;
    };
    brokerFilter: {
      value: { label: string; value: string }[];
    };
    productFilter: {
      value: { label: string; value: string }[];
    };
    statusFilter: {
      value: { label: string; value: string }[];
    };
    rateTypeFilter: {
      value: { label: string; value: string } [];
    };
    paymentTypeFilter: {
      value: { label: string; value: string } [];
    };
    itemFilter: {
      value: { label: string; value: string } [];
    }
  };
}

const BrokerRateSortFields = [
  { value: 'code', label: 'CODE' },
  { value: 'name', label: 'NAME' }
];


class BrokerRates extends React.Component<BrokerRateProps, BrokerRateState> {
  constructor(props: BrokerRateProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'ASC',
      sortedBy: 'CODE',
      filtersUpdated: false,
      loading: false,
      statistics: [],
      filter: {
        combinedFilter: {
          value: ''
        },
        brokerFilter: {
          value: []
        },
        productFilter: {
          value: []
        },
        statusFilter: {
          value: []
        },
        paymentTypeFilter: {
          value: []
        },
        rateTypeFilter: {
          value: []
        },
        itemFilter: {
          value: []
        }
      }
    };
  }

  async componentDidMount() {
    this.props.getBrokerList();
    await this.props.getProductList(true);
    this.props.getItemPriceList();
    this.props.getItemList();
    this.setState({ loading: true });
    await this.props.getBrokerRateList(this.props.filters ? this.props.filters : undefined);
    const statistics = await CoreApi.BrokerRates.getBrokerRatesStatistics();
    this.setState({ statistics: statistics.data });
    this.setState({ loading: false });
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) {
      this.updateFilterValues();
    }
  }

  updateFilterValues = () => {
    const filters = this.props.filters;
    if (filters) {
      const product = filters.productId ? this.props.products.find((p) => p.id === filters.productId) : undefined;
      const broker = filters.brokerId ? this.props.brokers.find((b) => b.id === filters.brokerId) : undefined;
      const item = filters.itemId ? this.props.items.find((i) => i.id === filters.itemId) : undefined;


      this.setState({
        selectedPageNumber: filters.page ? filters.page : 1,
        filtersUpdated: true,
        filter: {
          productFilter: {
            value: product ? [ { value: String(product.id), label: product.name } ] : []
          },
          brokerFilter: {
            value: broker ? [ { value: String(broker.id), label: broker.name } ] : []
          },
          statusFilter: {
            value: filters.status ? [ { value: filters.status, label: filters.status } ] : []
          },
          combinedFilter: {
            value: filters.combinedFilterField ? filters.combinedFilterField : ''
          },
          rateTypeFilter: {
            value: filters.rateType ? [ { value: filters.rateType, label: filters.rateType } ] : []
          },
          paymentTypeFilter: {
            value: filters.paymentType ? [ { value: filters.paymentType, label: filters.paymentType } ] : []
          },
          itemFilter: {
            value: item ? [ { value: String(item.id), label: item.name } ] : []
          }
        }
      });
    }
  };

  clearFilters = async () => {
    await this.setState({
      selectedPageNumber: 1,
      filtersUpdated: true,
      filter: {
        combinedFilter: {
          value: ''
        },
        productFilter: {
          value: []
        },
        brokerFilter: {
          value: []
        },
        statusFilter: {
          value: []
        },
        paymentTypeFilter: {
          value: []
        },
        rateTypeFilter: {
          value: []
        },
        itemFilter: {
          value: []
        }
      }
    });

    const filters = this.composeBrokerRateFilters();
    this.props.updateBrokerRateFilters(filters);
    await this.props.getBrokerRateList();
  };

  onRowClick = (rowInfo: any) =>
    this.props.history.push(`/setup/brokerrates/${rowInfo.original.code}/view`);

  brokerCell = ({ cell, row }: { cell: any, row: any }) => {
    const brokerObj = this.props.brokers.find((d) => d.id === row.original.brokerId);
    if (brokerObj) {
      return brokerObj.name;
    }
    return '';
  };

  productCell = ({ cell, row }: { cell: any, row: any }) => {
    const productObj = this.props.products.find((p) => p.id === row.original.productId);
    if (productObj) {
      return productObj.statusType === 'DELETED' ? `${productObj.name} (DELETED)` : productObj.name;
    }
    return '';
  };

  percentageCell = (value: any) => `${Math.trunc(value * 100)} %`;

  statusCell = (props: any) => (<StatusLabel status={props}/>);

  onFilterClick = async (e: FormEvent) => {
    e.preventDefault();
    const filters = this.composeBrokerRateFilters();
    await this.props.getBrokerRateList(filters);
    this.props.updateBrokerRateFilters(filters);
  };

  handleUpdateResults = async () => {
    const filters = this.composeBrokerRateFilters();
    await this.props.getBrokerRateList(filters);
    this.props.updateBrokerRateFilters(filters);
  };

  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = async (newValue: any, field: string): Promise<any> => {
    await this.promisedSetState({
      ...this.state,
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    });
    await this.handleUpdateResults();
  };

  promisedSetState = (newState: any) => new Promise((resolve: any) => this.setState(newState, resolve));

  composeBrokerRateFilters = () => {
    const broker: any = this.state.filter.brokerFilter.value;
    const product: any = this.state.filter.productFilter.value;
    const item: any = this.state.filter.itemFilter.value;
    const status: any = this.state.filter.statusFilter.value;
    const payment: any = this.state.filter.paymentTypeFilter.value;
    const rate: any = this.state.filter.rateTypeFilter.value;

    const filters: BrokerRateFilters = {
      brokerId: Number(broker.value),
      productId: Number(product.value),
      itemId: Number(item.value),
      status: status.value,
      paymentType: payment.value,
      rateType: rate.value,
      combinedFilterField: this.state.filter.combinedFilter.value,
      page: this.state.selectedPageNumber - 1
    };

    return filters;
  };

  latestActivityCell = (props: any) => {
    const { t } = this.props;
    if (this.state.statistics) {
      const brokerRateStatistics = this.state.statistics.find((b) => {
        return b.brokerRateCode === props;
      });
      const brokerRateStatisticsElement: string[] = [];
      if (brokerRateStatistics) {
        brokerRateStatisticsElement.push(
          `${t('text.broker_rates.statistics', {
            rates_count: brokerRateStatistics.rates,
            reservations_count: brokerRateStatistics.reservations
          })}`
        );
      } else {
        brokerRateStatisticsElement.push(
          `${t('text.broker_rates.statistics', { rates_count: 0, reservations_count: 0 })}`
        );
      }
      return brokerRateStatisticsElement;
    }

  };

  latestActivityHeader = (headerName: string, headerComment: string) => {
    return (
      <div>
        <div>{headerName}
          <img src={infoIcon} className="i-info-icon" id="latest-activity-header-tooltip"/>
        </div>
        <Tooltip
          className="dark-tooltip"
          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
          placement="right"
          target="latest-activity-header-tooltip"
          autohide={false}
        >
          <div className="d-flex flex-wrap">
            <pre className="dark-tooltip-text">{headerComment}</pre>
          </div>
        </Tooltip>
      </div>
    );
  };

  latestReservationCell = (props: any) => {
    const { t } = this.props;
    const brokerRateStatistics = this.state.statistics.find((b) => {
      return b.brokerRateCode === props;
    });

    if (brokerRateStatistics && brokerRateStatistics.latestReservationTimestamp) {
      const now = dayjs();
      const latestTimestamp = dayjs(brokerRateStatistics.latestReservationTimestamp);

      if (latestTimestamp.date() === now.date()) {
        return t('text.broker_rates.today');
      }

      const hours = now.diff(latestTimestamp, 'hours');
      const days = Math.ceil(hours / 24);

      if (days == 1) {
        return t('text.broker_rates.day_ago');
      } else if (days < 15) {
        return t('text.broker_rates.days_ago', { days: days });
      }

    }
    return t('text.broker_rates.over_days_ago');

  };

  getMandatoryItems = (props: any) => {
    return props?.length ?? 0;
  };

  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.broker_rates.code'), accessorKey: 'code', id: 'code', meta: { maxWidth: 200 }
      },
      { header: t('ct.broker_rates.name'), accessorKey: 'name', id: 'name', meta: { maxWidth: 250 } },
      {
        header: t('ct.broker_rates.broker'),
        accessorKey: 'brokerId',
        cell: ({ cell, row }: { cell: any, row: any }) => this.brokerCell({ cell, row }),
        meta: { maxWidth: 250 },
        id: 'brokerId'
      },
      {
        header: t('ct.broker_rates.product'),
        accessorKey: 'productId',
        cell: ({ cell, row }: { cell: any, row: any }) => this.productCell({ cell, row }),
        meta: { maxWidth: 150 },
        id: 'productId'
      },
      {
        header: t('ct.broker_rates.vat'),
        accessorKey: 'vatRate',
        id: 'vatRate',
        meta: { maxWidth: 100 },
        cell: (info: any) => this.percentageCell(info.getValue())
      },
      {
        header: t('ct.broker_rates.adjustment'),
        accessorKey: 'baseRateAdjustment',
        cell: (info: any) => this.percentageCell(info.getValue()),
        id: 'baseRateAdjustment',
        meta: { maxWidth: 100 },
        style: { textAlign: 'right' }
      },
      {
        header: () => this.latestActivityHeader(t('ct.broker_rates.latest_activity'), t('comment.broker_rates.latest_activity_tooltip')),
        accessorKey: 'code',
        cell: (info: any) => this.latestActivityCell(info.getValue()),
        meta: {
          minWidth: 230,
          maxWidth: 300
        },
        id: 'id' + 'latestActivity'
      },
      {
        header: t('ct.broker_rates.latest_reservation'),
        accessorKey: 'code',
        cell: (info: any) => this.latestReservationCell(info.getValue()),
        meta: { maxWidth: 150 },
        id: 'id' + 'latestReservation'
      },
      {
        header: 'Mandatory items',
        accessorKey: 'itemPrices',
        cell: (info: any) => this.getMandatoryItems(info.getValue()),
        style: { textAlign: 'right' },
        headerClassName: 'numeric-column',
        meta: { maxWidth: 130 },
        id: 'itemPrices'
      },
      {
        header: t('ct.broker_rates.status'),
        accessorKey: 'statusType',
        cell: (info: any) => this.statusCell(info.getValue()),
        meta: { maxWidth: 100 },
        id: 'statusType'
      }
    ];
  }

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    const sortFieldName = BrokerRateSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: sortFieldName.label });
    return (
      this.props.getBrokerRateList(this.composeBrokerRateFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortField !== undefined && pageRequest.sortDirection !== undefined) {
      const pageRequestFix = {
        page: pageRequest.page,
        sortDirection: pageRequest.sortDirection,
        sortField: BrokerRateSortFields.find((column) => column.label === pageRequest.sortField)?.label
      };
      this.props.getBrokerRateList(this.composeBrokerRateFilters(), pageRequestFix);

      this.setState({
        selectedPageNumber: pageRequestFix.page + 1,
        sortOrder: pageRequestFix.sortDirection ?? 'ASC',
        sortedBy: BrokerRateSortFields.find((column) => column.label === pageRequest.sortField)?.label ?? 'CODE'
      });
    }
  }

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>{t('page_title.broker_rates')}</h1>
            {this.props.user && userCanAccessComponent(this.props.user, brokerRateEditRoles) && (
              <Link
                to={{
                  pathname: '/setup/brokerrates/create'
                }}
                className="btn btn-primary btn-wide"
              >
                {t('b.add_new').toUpperCase()}
              </Link>
            )}
          </div>
          <form className="d-flex justify-content-between container-fluid filter-container form-group form-inline">
            <div className="d-flex flex-wrap w-75 filters-container">
              <input
                type="text"
                value={this.state.filter.combinedFilter.value}
                name="combinedFilter"
                className="form-control col edit-form-input margins pl-1 ml-1 min-width-form-input filter-item-margin"
                placeholder={t('ph.filter.broker_rates.by_code')}
                onChange={this.changeHandler}
              />
              <Select
                className="mr-1"
                placeholder={t('ph.filter.broker_rates.by_broker')}
                // eslint-disable-next-line no-nested-ternary
                options={this.props.brokers.sort((a, b) => ((a.name < b.name)
                  ? -1 : (a.name > b.name) ? 1 : 0)).map((b) => ({ label: b.name, value: String(b.id) }))}
                value={this.state.filter.brokerFilter.value}
                equalWidthElement
                marginTop
                onChange={async (val) => this.dropdownChange(val, 'brokerFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="brokers"
              />
              <Select
                className="mr-1"
                placeholder={t('ph.filter.broker_rates.by_product')}
                equalWidthElement
                marginTop
                options={this.props.products.filter((p) => p.statusType !== 'DELETED').map((p) =>
                  ({ label: p.name, value: String(p.id) })
                )}
                value={this.state.filter.productFilter.value}
                onChange={(val) => this.dropdownChange(val, 'productFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="products"
              />
              <Select
                className="mr-1"
                placeholder={t('ph.filter.broker_rates.by_mandatory_item')}
                equalWidthElement
                marginTop
                options={this.props.items.map((i) => ({ label: i.name, value: String(i.id) }))}
                value={this.state.filter.itemFilter.value}
                onChange={(val) => this.dropdownChange(val, 'itemFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="items"
              />
              <Select
                value={this.state.filter.statusFilter.value}
                className="mr-1"
                marginTop
                equalWidthElement
                placeholder={t('ph.filter.broker_rates.by_status')}
                options={[
                  { value: 'ACTIVE', label: 'Active' },
                  { value: 'INACTIVE', label: 'Inactive' }
                ]}
                onChange={(val) => this.dropdownChange(val, 'statusFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="status"
              />
              <Select
                value={this.state.filter.paymentTypeFilter.value}
                className="mr-1"
                marginTop
                equalWidthElement
                placeholder={t('ph.filter.broker_rates.by_payment_type')}
                options={paymentTypeOptions}
                onChange={(val) => this.dropdownChange(val, 'paymentTypeFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="paymentType"
              />
              <Select
                value={this.state.filter.rateTypeFilter.value}
                className="mr-1"
                marginTop
                equalWidthElement
                placeholder={t('ph.filter.broker_rates.by_rate_type')}
                options={rateTypeOptions}
                onChange={(val) => this.dropdownChange(val, 'rateTypeFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="rateType"
              />
            </div>
            <div className="pl-1">
              <button type="button" className="btn btn-text-only mr-2" onClick={() => this.clearFilters()}>{
                t('b.clear_filter')
              }</button>
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>{
                t('b.filter').toUpperCase()
              }</button>
            </div>
          </form>
          {this.state.loading ? <LoaderBar/> : null}
          {!this.state.loading && this.props.brokerRates
            ? (
              <div className="table-list">
                <ListViewTable
                  data={this.props.brokerRates}
                  columns={this.defineColumns()}
                  getTrProps={this.onRowClick}
                  sortBy={this.sortBy}
                  sortOrder={this.state.sortOrder}
                  sortedBy={BrokerRateSortFields.find(({ value, label }) => this.state.sortedBy === label)?.value}
                  paginationEnabled={true}
                  totalPages={this.props.totalPages}
                  totalElements={this.props.totalElements}
                  selectedPageNumber={this.state.selectedPageNumber}
                  getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
                />
              </div>
            ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  brokerRates: state.setup.brokerRates.brokerRates,
  brokers: state.admin.supplierBrokers.supplierBrokers,
  products: state.setup.products.products,
  itemPrices: state.rates.itemPrices.itemPrices,
  items: state.setup.items.items,
  totalElements: state.setup.brokerRates.totalElements,
  totalPages: state.setup.brokerRates.totalPages,
  filters: state.setup.brokerRates.filters,
  user: state.authentication.user
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, BrokerRateActionTypes>) => ({
  getBrokerRateList: (filters?: BrokerRateFilters, page?: PageRequest) => dispatch(getBrokerRateList(filters, page)),
  getItemPriceList: () => dispatch(getItemPriceList()),
  getItemList: () => dispatch(getItemList()),
  getBrokerList: () => dispatch(getSupplierBrokerList()),
  getProductList: (isDeletedIncluded: boolean) => dispatch(getProductList(undefined, undefined, isDeletedIncluded)),
  updateBrokerRateFilters: (filters: BrokerRateFilters) => dispatch(updateBrokerRateFilters(filters))
});
export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(BrokerRates));
